import { useCallback, useState } from "react";
import { CircleCheck } from "lucide-react";

import { Card } from "../../shared/card/card.tsx";
import { WizardRailLayout } from "../onboarding-shared/wizard-rail-layout.tsx";
import { ApiError } from "../../../utils/api.ts";
import { showError, showSuccess } from "../../../utils/toast.ts";
import { PHASE2_DOC_SLOTS, type DocumentFilesState, type Phase2DocType } from "./doc-types.ts";
import { usePostjoinOtp } from "../onboard-verify/hooks/use-postjoin-otp.ts";
import { VerifyEmailOtpModal } from "../onboard-verify/modals/verify-email-otp-modal.tsx";
import { useOnboardPostJoinPhase2 } from "./hooks/use-onboard-post-join-phase2.ts";
import type { BankStepValues, Phase2ValidateSuccess } from "./schemas/onboard-post-join.schema.ts";
import { validatePhase2DocumentFile } from "./utils/validate-phase2-document-file.ts";
import { BankStep } from "./steps/bank-step.tsx";
import { DocumentsStep } from "./steps/documents-step.tsx";
import { ReviewStep } from "./steps/review-step.tsx";
import styles from "./onboard-post-join-wizard.module.css";

const STEPS = [
  { id: "bank", label: "Bank Details" },
  { id: "docs", label: "Documents" },
  { id: "review", label: "Review" },
] as const;

const SECTION_TITLES = ["Bank Details", "Documents", "Review"];

const EMPTY_BANK: BankStepValues = {
  beneficiaryName: "",
  accountNumber: "",
  bankName: "",
  branchName: "",
  ifscCode: "",
};

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

export type OnboardPostJoinWizardProps = {
  token: string;
  /** Called after email + token validate succeeds so the host page can personalize the header. */
  onEmployeeVerified?: (employeeName: string) => void;
  onSubmitted?: () => void;
};

export function OnboardPostJoinWizard({
  token,
  onEmployeeVerified,
  onSubmitted,
}: OnboardPostJoinWizardProps) {
  const { saveBankDetails, uploadDocument, submitPhase2 } = useOnboardPostJoinPhase2();
  const { generateOtp, validateOtp } = usePostjoinOtp();

  const [profile, setProfile] = useState<Phase2ValidateSuccess | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [bank, setBank] = useState<BankStepValues>(EMPTY_BANK);
  const [cancelledCheque, setCancelledCheque] = useState<File | null>(null);
  const [documentFiles, setDocumentFiles] = useState<DocumentFilesState>({});
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);

  const onGenerateOtp = useCallback(
    (email: string) => generateOtp(token, email),
    [token, generateOtp],
  );

  const onValidate = useCallback(
    async (email: string, otp: string) => {
      const data = await validateOtp(token, email, otp);
      setProfile(data);
      onEmployeeVerified?.(data.employeeName);
    },
    [token, validateOtp, onEmployeeVerified],
  );

  const onBankNext = useCallback(
    async (values: BankStepValues, cheque: File) => {
      if (!profile) return;
      const sizeErr = validatePhase2DocumentFile(cheque);
      if (sizeErr) {
        showError(sizeErr);
        return;
      }
      setBusy(true);
      try {
        await saveBankDetails(profile.employeeId, values, cheque);
        setBank(values);
        setCancelledCheque(cheque);
        setStepIndex(1);
      } catch (e) {
        showError(errorMessage(e));
      } finally {
        setBusy(false);
      }
    },
    [profile, saveBankDetails],
  );

  const onDocsNext = useCallback(async () => {
    if (!profile) return;
    for (const slot of PHASE2_DOC_SLOTS) {
      if (slot.required && !documentFiles[slot.docType]) {
        showError(`Please upload: ${slot.label}.`);
        return;
      }
    }
    for (const slot of PHASE2_DOC_SLOTS) {
      const file = documentFiles[slot.docType];
      if (!file) continue;
      const sizeErr = validatePhase2DocumentFile(file);
      if (sizeErr) {
        showError(sizeErr);
        return;
      }
    }
    setBusy(true);
    try {
      for (const slot of PHASE2_DOC_SLOTS) {
        const file = documentFiles[slot.docType];
        if (file) {
          await uploadDocument(profile.employeeId, slot.docType, file);
        }
      }
      setStepIndex(2);
    } catch (e) {
      showError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [documentFiles, profile, uploadDocument]);

  const onFinalSubmit = useCallback(async () => {
    if (!profile) return;
    setBusy(true);
    try {
      await submitPhase2(profile.employeeId);
      setFinished(true);
      onSubmitted?.();
      showSuccess("Your submission has been sent successfully.");
    } catch (e) {
      showError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [profile, submitPhase2, onSubmitted]);

  const setDocFile = useCallback((docType: Phase2DocType, file: File | null) => {
    setDocumentFiles((prev) => ({ ...prev, [docType]: file }));
  }, []);

  if (!profile) {
    return (
      <>
        <p className={`body-sm ${styles.verifyHint}`}>
          Verify your email in the dialog to continue onboarding.
        </p>
        <VerifyEmailOtpModal
          open={true}
          variant="work"
          token={token}
          onGenerateOtp={onGenerateOtp}
          onValidate={onValidate}
        />
      </>
    );
  }

  if (finished) {
    return (
      <Card variant="subtle" padding="5xl" className={styles.card}>
        <div className={styles.successMark} aria-hidden>
          <CircleCheck size={88} strokeWidth={1.35} />
        </div>
        <h2 className="heading-lg text-center">Thank you!</h2>
        <p className={`body-sm ${styles.lead} text-center`}>
          Your submission has been sent successfully.
          <br />
          HR will get back to you shortly.
        </p>
      </Card>
    );
  }

  const isLast = stepIndex === STEPS.length - 1;
  const formId = isLast ? undefined : `post-step-${stepIndex}`;

  return (
    <div className={styles.wrap}>
      <WizardRailLayout
        steps={[...STEPS]}
        currentStepIndex={stepIndex}
        sectionTitle={SECTION_TITLES[stepIndex] ?? "Review"}
        onBack={() => setStepIndex((i) => Math.max(0, i - 1))}
        primaryLabel={isLast ? "Submit" : "Continue"}
        primaryLoading={busy}
        primaryFormId={formId}
        onPrimary={isLast ? () => void onFinalSubmit() : undefined}
      >
        {stepIndex === 0 ? (
          <BankStep
            formId="post-step-0"
            hideFooter
            defaultValues={bank}
            cancelledCheque={cancelledCheque}
            onCancelledChequeChange={setCancelledCheque}
            loading={busy}
            onNext={onBankNext}
          />
        ) : null}
        {stepIndex === 1 ? (
          <DocumentsStep
            formId="post-step-1"
            hideFooter
            files={documentFiles}
            onFileChange={setDocFile}
            loading={busy}
            onBack={() => setStepIndex(0)}
            onNext={onDocsNext}
          />
        ) : null}
        {stepIndex === 2 ? (
          <ReviewStep
            hideFooter
            bank={bank}
            cancelledCheque={cancelledCheque}
            documentFiles={documentFiles}
            loading={busy}
            onBack={() => setStepIndex(1)}
            onSubmit={onFinalSubmit}
          />
        ) : null}
      </WizardRailLayout>
    </div>
  );
}
